import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, Target, Flame, TrendingUp, Star, 
  CheckCircle2, BarChart3, Award, Calendar,
  ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const texts = {
  de: {
    title: 'Produktivität',
    points: 'Punkte',
    streak: 'Serie',
    bestStreak: 'Beste Serie',
    days: 'Tage',
    weeklyChallenge: 'Wochen-Challenges',
    completed: 'Erledigt',
    progress: 'Fortschritt',
    claim: 'Abholen',
    allCompleted: 'Alle erledigt!',
    bonusEarned: 'Bonus verdient',
    todayStats: 'Heute',
    weeklyStats: 'Diese Woche',
    created: 'Erstellt',
    done: 'Erledigt',
    viewDashboard: 'Dashboard ansehen',
    productivityDashboard: 'Produktivitäts-Dashboard',
    dailyActivity: 'Aktivität (7 Tage)',
    topCategories: 'Top Kategorien',
    completionRate: 'Erledigungsrate',
    monthlyStats: 'Monatlich',
    totalPoints: 'Gesamtpunkte',
    noCategories: 'Noch keine Kategorien',
    leaderboard: 'Bestenliste',
    yourRank: 'Dein Rang',
    close: 'Schließen'
  },
  en: {
    title: 'Productivity',
    points: 'Points',
    streak: 'Streak',
    bestStreak: 'Best Streak',
    days: 'days',
    weeklyChallenge: 'Weekly Challenges',
    completed: 'Completed',
    progress: 'Progress',
    claim: 'Claim',
    allCompleted: 'All completed!',
    bonusEarned: 'Bonus earned',
    todayStats: 'Today',
    weeklyStats: 'This Week',
    created: 'Created',
    done: 'Done',
    viewDashboard: 'View Dashboard',
    productivityDashboard: 'Productivity Dashboard',
    dailyActivity: 'Activity (7 days)',
    topCategories: 'Top Categories',
    completionRate: 'Completion Rate',
    monthlyStats: 'Monthly',
    totalPoints: 'Total Points',
    noCategories: 'No categories yet',
    leaderboard: 'Leaderboard',
    yourRank: 'Your Rank',
    close: 'Close'
  }
};

// Challenge Card Component
const ChallengeCard = ({ challenge, language, onClaim }) => {
  const t = texts[language] || texts.de;
  const title = language === 'de' ? challenge.title_de : challenge.title_en;
  const description = language === 'de' ? challenge.description_de : challenge.description_en;
  
  return (
    <div className={`p-3 rounded-lg border ${
      challenge.is_completed 
        ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800' 
        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {challenge.is_completed ? (
              <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            ) : (
              <Target className="h-4 w-4 text-slate-400 shrink-0" />
            )}
            <span className="font-medium text-sm truncate">{title}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-sm font-bold text-[#006039]">+{challenge.points}</span>
        </div>
      </div>
      
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span>{challenge.current_progress}/{challenge.target}</span>
          <span>{challenge.percentage}%</span>
        </div>
        <Progress value={challenge.percentage} className="h-1.5" />
      </div>
      
      {challenge.percentage >= 100 && !challenge.is_completed && (
        <Button 
          size="sm" 
          className="w-full mt-2 bg-[#006039] hover:bg-[#004d2e]"
          onClick={() => onClaim(challenge.id)}
        >
          <Award className="h-3 w-3 mr-1" />
          {t.claim}
        </Button>
      )}
    </div>
  );
};

// Mini Activity Chart
const ActivityChart = ({ data, language }) => {
  const maxValue = Math.max(...data.map(d => d.created + d.completed), 1);
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((day, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-0.5">
          <div className="w-full flex flex-col gap-0.5">
            <div 
              className="w-full bg-green-500 rounded-t"
              style={{ height: `${(day.completed / maxValue) * 40}px` }}
              title={`${day.completed} ${language === 'de' ? 'erledigt' : 'completed'}`}
            />
            <div 
              className="w-full bg-blue-400 rounded-b"
              style={{ height: `${(day.created / maxValue) * 40}px` }}
              title={`${day.created} ${language === 'de' ? 'erstellt' : 'created'}`}
            />
          </div>
          <span className="text-[10px] text-muted-foreground">{day.day_name}</span>
        </div>
      ))}
    </div>
  );
};

export const PinboardGamification = ({ language = 'de' }) => {
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEnabled, setIsEnabled] = useState(true); // Track if gamification is enabled
  
  const t = texts[language] || texts.de;

  const getToken = () => localStorage.getItem('token');

  const fetchData = async () => {
    try {
      const token = getToken();
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, challengesRes] = await Promise.all([
        fetch(`${API_URL}/api/pinboard/gamification/stats`, { headers }),
        fetch(`${API_URL}/api/pinboard/gamification/challenges`, { headers })
      ]);
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        // Check if gamification is disabled
        if (statsData.gamification_enabled === false) {
          setIsEnabled(false);
          setLoading(false);
          return;
        }
        setStats(statsData);
      }
      if (challengesRes.ok) setChallenges(await challengesRes.json());
    } catch (error) {
      console.error('Error fetching gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductivity = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/pinboard/gamification/productivity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setProductivity(await res.json());
    } catch (error) {
      console.error('Error fetching productivity:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClaimChallenge = async (challengeId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/pinboard/gamification/complete-challenge/${challengeId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`🎉 +${result.total_points_earned} ${t.points}!`);
        fetchData();
      }
    } catch (error) {
      toast.error('Fehler beim Abholen der Belohnung');
    }
  };

  const openDashboard = () => {
    fetchProductivity();
    setShowDashboard(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-[#006039]" />
      </div>
    );
  }

  // Don't render anything if gamification is disabled
  if (!isEnabled) {
    return null;
  }

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#006039]/5 to-[#006039]/10 rounded-xl p-4 border border-[#006039]/20"
      >
        {/* Header Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Points */}
            <div className="flex items-center gap-2">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{stats?.total_points || 0}</div>
                <div className="text-xs text-muted-foreground">{t.points}</div>
              </div>
            </div>
            
            {/* Streak */}
            <div className="flex items-center gap-2">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Flame className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{stats?.current_streak || 0}</div>
                <div className="text-xs text-muted-foreground">{t.streak}</div>
              </div>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={openDashboard}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {t.viewDashboard}
          </Button>
        </div>
        
        {/* Today/Week Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">{t.todayStats}</div>
            <div className="flex items-center gap-3">
              <span className="text-sm">{stats?.today?.notes_created || 0} {t.created}</span>
              <span className="text-sm text-green-600">{stats?.today?.notes_completed || 0} {t.done}</span>
            </div>
          </div>
          <div className="bg-white/50 dark:bg-slate-800/50 rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">{t.weeklyStats}</div>
            <div className="flex items-center gap-3">
              <span className="text-sm">{stats?.weekly?.notes_created || 0} {t.created}</span>
              <span className="text-sm text-green-600">{stats?.weekly?.notes_completed || 0} {t.done}</span>
            </div>
          </div>
        </div>
        
        {/* Weekly Challenges */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium flex items-center gap-2">
              <Trophy className="h-4 w-4 text-[#006039]" />
              {t.weeklyChallenge}
            </h4>
            {challenges?.all_completed && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                {t.allCompleted} +{challenges.bonus_earned}
              </span>
            )}
          </div>
          
          <div className="space-y-2">
            {challenges?.challenges?.map((challenge) => (
              <ChallengeCard 
                key={challenge.id}
                challenge={challenge}
                language={language}
                onClaim={handleClaimChallenge}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Full Dashboard Dialog */}
      <Dialog open={showDashboard} onOpenChange={setShowDashboard}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" aria-describedby="dashboard-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#006039]" />
              {t.productivityDashboard}
            </DialogTitle>
            <DialogDescription id="dashboard-description">
              {language === 'de' 
                ? 'Übersicht über deine Pinwand-Aktivität und Fortschritte'
                : 'Overview of your pinboard activity and progress'}
            </DialogDescription>
          </DialogHeader>
          
          {productivity ? (
            <div className="space-y-6 pt-4">
              {/* Top Stats Row */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/30 dark:to-yellow-800/20 rounded-xl p-4 text-center">
                  <Star className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{productivity.gamification?.total_points || 0}</div>
                  <div className="text-xs text-muted-foreground">{t.totalPoints}</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/30 dark:to-orange-800/20 rounded-xl p-4 text-center">
                  <Flame className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{productivity.gamification?.current_streak || 0}</div>
                  <div className="text-xs text-muted-foreground">{t.streak}</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/20 rounded-xl p-4 text-center">
                  <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold">{productivity.totals?.completion_rate || 0}%</div>
                  <div className="text-xs text-muted-foreground">{t.completionRate}</div>
                </div>
              </div>
              
              {/* Activity Chart */}
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {t.dailyActivity}
                </h4>
                <ActivityChart data={productivity.daily_activity || []} language={language} />
                <div className="flex items-center justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-400 rounded" /> {t.created}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded" /> {t.done}
                  </span>
                </div>
              </div>
              
              {/* Monthly Stats & Categories */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                  <h4 className="font-medium mb-3">{t.monthlyStats}</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">{t.created}</span>
                      <span className="font-medium">{productivity.monthly?.created || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">{t.done}</span>
                      <span className="font-medium text-green-600">{productivity.monthly?.completed || 0}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                  <h4 className="font-medium mb-3">{t.topCategories}</h4>
                  {productivity.top_categories?.length > 0 ? (
                    <div className="space-y-1">
                      {productivity.top_categories.map((cat, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="truncate">{cat.name}</span>
                          <span className="text-muted-foreground">{cat.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t.noCategories}</p>
                  )}
                </div>
              </div>
              
              {/* Best Streak */}
              <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
                <div className="flex items-center gap-3">
                  <Trophy className="h-6 w-6 text-orange-600" />
                  <div>
                    <div className="font-medium">{t.bestStreak}</div>
                    <div className="text-sm text-muted-foreground">
                      {productivity.gamification?.best_streak || 0} {t.days}
                    </div>
                  </div>
                </div>
                <div className="text-3xl font-bold text-orange-600">
                  {productivity.gamification?.best_streak || 0}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#006039]" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PinboardGamification;
